import {
  AgentView,
  ApiErrorBody,
  CanvasView,
  CommandBatchOutput,
  CreateExportOutput,
  CreateGenerationOutput,
  CreateProjectOutput,
  EstimateOutput,
  EpisodeRef,
  ExportJob,
  FileMeta,
  GenerationJob,
  GenerationList,
  HistoryList,
  MeSchema,
  NavMenuSchema,
  OkSchema,
  OutlineView,
  OutputLine,
  OutputList,
  ProjectDetail,
  ProjectList,
  PutFileOutput,
  RevertOutput,
  ScriptView,
  SendAgentMessageOutput,
  StoryboardView,
  TermList,
  TimelineView,
  UploadOutput,
  UsageSummary,
  WorldNode,
  WorldView,
  type CommandBatchInput,
  type CreateExportInput,
  type CreateGenerationInput,
  type CreateLibraryInput,
  type CreateOutputInput,
  type CreateProjectInput,
  type CreateShareInput,
  type ErrorCode,
  type LibraryKind,
  type PatchLibraryInput,
  type PatchOutputInput,
  type PatchProjectInput,
  type PutFileInput,
  type RevertInput,
  type SendAgentMessageInput,
  type ShareLink,
  type ViewKind,
  type WorldImageKind,
} from "@dramapie/shared";

import {
  libraryEntityFromPayload,
  parseLibraryList,
  parseShareList,
  shareLinkFromPayload,
  type LibraryEntityView as LibraryEntityViewData,
} from "./m5";

// ---------------------------------------------------------------- 错误

/** 契约里的错误码 + 两个传输层错误码。 */
export type ApiErrorCode = ErrorCode | "network_error" | "invalid_response";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  /**
   * 原始错误响应体。409 `revision_conflict` 的 `{ rev, view }` 不在
   * `error` 对象里，靠它把最新投影还给调用方。
   */
  readonly payload?: unknown;

  constructor(
    code: ApiErrorCode,
    status: number,
    message: string,
    details?: Record<string, unknown>,
    payload?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
    this.payload = payload;
  }
}

/** 只依赖 safeParse 的结构，zod schema 直接可用。 */
type Schema<T> = {
  safeParse: (input: unknown) => { success: true; data: T } | { success: false; error: unknown };
};

const fallbackCodeByStatus: Record<number, ErrorCode> = {
  400: "bad_request",
  401: "unauthorized",
  402: "insufficient_points",
  403: "forbidden",
  404: "project_not_found",
  409: "revision_conflict",
  422: "validation_failed",
  500: "internal",
};

type ParsedError = {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
  payload?: unknown;
};

async function parseErrorBody(res: Response): Promise<ParsedError> {
  const fallback = fallbackCodeByStatus[res.status] ?? "internal";
  try {
    const body: unknown = await res.json();
    const parsed = ApiErrorBody.safeParse(body);
    if (parsed.success) return { ...parsed.data.error, payload: body };
    // 合法 JSON 但不是标准错误体（例如 409 的 { rev, view }），
    // 保留 body 供 conflictViewFrom 取用，code 走状态码兜底。
    return { code: fallback, message: `请求失败（HTTP ${res.status}）`, payload: body };
  } catch {
    // 非 JSON 错误体（网关 / 代理返回的 HTML），退回状态码映射。
  }
  return { code: fallback, message: `请求失败（HTTP ${res.status}）` };
}

async function throwHttpError(res: Response, redirectOnUnauthorized: boolean): Promise<never> {
  const parsed = await parseErrorBody(res);
  if (res.status === 401 && redirectOnUnauthorized) {
    // 先跳转再抛错：调用方仍然能拿到 ApiError 做兜底渲染。
    unauthorizedHandler();
  }
  throw new ApiError(parsed.code, res.status, parsed.message, parsed.details, parsed.payload);
}

// ---------------------------------------------------------------- 401 跳登录

function defaultUnauthorizedHandler(): void {
  if (typeof window === "undefined") return;
  // /login 自己会请求 /api/me 判断登录态，不要再跳，避免循环。
  if (window.location.pathname === "/login") return;
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}

let unauthorizedHandler: () => void = defaultUnauthorizedHandler;

/** 替换 401 行为（测试用；也可以在宿主里接管跳转）。 */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export function resetUnauthorizedHandler(): void {
  unauthorizedHandler = defaultUnauthorizedHandler;
}

// ---------------------------------------------------------------- 请求底座

const API_PREFIX = "/api";

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface RequestOptions {
  signal?: AbortSignal;
  /** 401 时是否跳登录页，默认 true；/login 的 /api/me 探测传 false。 */
  redirectOnUnauthorized?: boolean;
  /**
   * 单次请求超时（毫秒）。只给只读投影端点用：线上出现过单个请求
   * 一直不 settle 导致 React Query 永远 pending 的情况；超时后由查询自己重试。
   * 上传 / 导出等长请求不要传。
   */
  timeoutMs?: number;
}

interface RequestInitLite extends RequestOptions {
  method?: Method;
  body?: unknown;
  headers?: Record<string, string>;
  idempotencyKey?: string;
}

async function request<T>(
  path: string,
  schema: Schema<T> | null,
  init: RequestInitLite = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...init.headers };
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  if (init.idempotencyKey) headers["Idempotency-Key"] = init.idempotencyKey;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let signal = init.signal;
  if (init.timeoutMs !== undefined && init.timeoutMs > 0 && !signal) {
    const controller = new AbortController();
    // 计时器覆盖到 body 解析结束：线上出现过「响应头到了、body 一直不结束」的挂起。
    timeoutId = setTimeout(() => controller.abort(), init.timeoutMs);
    signal = controller.signal;
  }

  try {
    let res: Response;
    try {
      res = await fetch(`${API_PREFIX}${path}`, {
        method: init.method ?? "GET",
        headers,
        credentials: "same-origin",
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // 自己的超时也要变成可重试的错误，而不是被当成「用户取消」。
        if (timeoutId !== undefined) {
          throw new ApiError("network_error", 0, "请求超时，请重试");
        }
        throw error;
      }
      throw new ApiError("network_error", 0, "网络请求失败，请检查网络后重试");
    }

    if (!res.ok) {
      await throwHttpError(res, init.redirectOnUnauthorized !== false);
    }

    if (res.status === 204 || schema === null) return undefined as T;

    let payload: unknown;
    try {
      payload = await res.json();
    } catch (error) {
      if (timeoutId !== undefined && error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("network_error", 0, "请求超时，请重试");
      }
      throw new ApiError("invalid_response", res.status, "服务返回的不是合法 JSON");
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError("invalid_response", res.status, "服务返回的数据不符合契约");
    }
    return parsed.data;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

function projectPath(projectId: string): string {
  return `/projects/${encodeURIComponent(projectId)}`;
}

/** 逐段编码，保留 `/` 作为路径分隔符。 */
function encodeFilePath(path: string): string {
  return path
    .split("/")
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------- 账号

export function getMe(options: RequestOptions = {}) {
  return request("/me", MeSchema, options);
}

export function logout(options: RequestOptions = {}) {
  return request("/auth/logout", OkSchema, { ...options, method: "POST" });
}

/**
 * 跨站产品菜单（P1-W3/W4）。后端保证「永不失败」：Plan 挂了也返回内置三条，
 * 所以这里不需要重试，失败时组件用同一份 shared 常量兜底。
 */
export function getNav(options: RequestOptions = {}) {
  return request("/nav", NavMenuSchema, { timeoutMs: 5_000, ...options });
}

/**
 * 充值回站后主动刷新余额/权益（服务端 fail-soft：上游挂了也返回旧快照）。
 * 与 `getMe` 同一个响应形状，调用方一般只关心随后 invalidate `["me"]`。
 */
export function refreshBilling(options: RequestOptions = {}) {
  return request("/billing/refresh", MeSchema, { ...options, method: "POST" });
}

// ---------------------------------------------------------------- 项目

export function getProjects(options: RequestOptions = {}) {
  return request("/projects", ProjectList, options);
}

export function createProject(
  input: CreateProjectInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
) {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  return request("/projects", CreateProjectOutput, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export function getProject(projectId: string, options: RequestOptions = {}) {
  return request(projectPath(projectId), ProjectDetail, { timeoutMs: 8_000, ...options });
}

export function patchProject(
  projectId: string,
  input: PatchProjectInput,
  options: RequestOptions = {},
) {
  return request(projectPath(projectId), ProjectDetail, { ...options, method: "PATCH", body: input });
}

export function deleteProject(projectId: string, options: RequestOptions = {}) {
  return request<void>(projectPath(projectId), null, { ...options, method: "DELETE" });
}

// ---------------------------------------------------------------- 产出线（M8）

/** 全量产出线（含 hidden / archived）：左栏浮层与管理要用全量。 */
export function listOutputs(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/outputs`, OutputList, options);
}

export function createOutput(
  projectId: string,
  input: CreateOutputInput,
  options: RequestOptions = {},
) {
  return request(`${projectPath(projectId)}/outputs`, OutputLine, {
    ...options,
    method: "POST",
    body: input,
  });
}

export function patchOutput(
  projectId: string,
  outputId: string,
  patch: PatchOutputInput,
  options: RequestOptions = {},
) {
  return request(`${projectPath(projectId)}/outputs/${encodeURIComponent(outputId)}`, OutputLine, {
    ...options,
    method: "PATCH",
    body: patch,
  });
}

export function deleteOutput(projectId: string, outputId: string, options: RequestOptions = {}) {
  return request<void>(
    `${projectPath(projectId)}/outputs/${encodeURIComponent(outputId)}`,
    null,
    { ...options, method: "DELETE" },
  );
}

/**
 * 把某一集移到别的产出线（契约 §3「删线前先把集移走」）。
 * 服务端改的是 storyboard 文档里的 `episode.outputId`，所以这一次移动可撤销。
 */
export function moveEpisode(
  projectId: string,
  episodeId: string,
  outputId: string,
  options: RequestOptions = {},
) {
  return request(
    `${projectPath(projectId)}/episodes/${encodeURIComponent(episodeId)}`,
    EpisodeRef,
    { ...options, method: "PATCH", body: { outputId } },
  );
}

// ---------------------------------------------------------------- 视图

export function getStoryboard(
  projectId: string,
  params: { episode?: string; kind?: ViewKind } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams({ kind: params.kind ?? "storyboard" });
  if (params.episode) query.set("episode", params.episode);
  return request(`${projectPath(projectId)}/view?${query.toString()}`, StoryboardView, {
    timeoutMs: 8_000,
    ...options,
  });
}

/**
 * M7 世界树（契约 M7 §2）：节点 + 规则。
 * 投影只给 `WorldNode.sceneIds`，场次数据仍从 storyboard 取。
 */
export function getWorld(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/view?kind=world`, WorldView, {
    timeoutMs: 8_000,
    ...options,
  });
}

function worldImagesPath(projectId: string, nodeId: string): string {
  return `${projectPath(projectId)}/world/${encodeURIComponent(nodeId)}/images`;
}

async function parseWorldNodeResponse(res: Response): Promise<WorldNode> {
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError("invalid_response", res.status, "服务返回的不是合法 JSON");
  }
  const parsed = WorldNode.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError("invalid_response", res.status, "服务返回的数据不符合契约");
  }
  return parsed.data;
}

/**
 * 上传世界节点设定图（multipart：`file` + 表单 `kind`）。
 * 响应是更新后的 `WorldNode`（图片带现签 `url`），调用方不必再拉一遍投影。
 */
export async function uploadWorldImage(
  projectId: string,
  nodeId: string,
  kind: WorldImageKind,
  file: File,
  options: RequestOptions = {},
): Promise<WorldNode> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file);

  let res: Response;
  try {
    res = await fetch(`${API_PREFIX}${worldImagesPath(projectId, nodeId)}`, {
      method: "POST",
      body: form,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("network_error", 0, "设定图上传失败，请检查网络后重试");
  }
  if (!res.ok) await throwHttpError(res, options.redirectOnUnauthorized !== false);
  return parseWorldNodeResponse(res);
}

/** 删除第 `index` 张设定图（数组下标，服务端走提交闸门）；响应是更新后的节点。 */
export function deleteWorldImage(
  projectId: string,
  nodeId: string,
  index: number,
  options: RequestOptions = {},
) {
  return request(`${worldImagesPath(projectId, nodeId)}/${index}`, WorldNode, {
    ...options,
    method: "DELETE",
  });
}

/** M7 大纲树（契约 M7 §2）：卷 → 单元 → 集，每级带真实正文。 */
export function getOutline(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/view?kind=outline`, OutlineView, {
    timeoutMs: 8_000,
    ...options,
  });
}
/** M7 剧本 / 分镜稿（契约 M7 §2）：beats + scenes + rows。 */
export function getScript(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/view?kind=script`, ScriptView, {
    timeoutMs: 8_000,
    ...options,
  });
}

/** M7 术语表（契约 M7 §2）。 */
export function getTerms(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/view?kind=terms`, TermList, {
    timeoutMs: 8_000,
    ...options,
  });
}

/** M6 只读画布（契约 M6 §1）：场容器 + 镜头卡，布局由服务端算。 */
export function getCanvas(
  projectId: string,
  params: { episode?: string; focus?: string } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams();
  if (params.episode) query.set("episode", params.episode);
  if (params.focus) query.set("focus", params.focus);
  const suffix = query.toString();
  return request(
    `${projectPath(projectId)}/canvas${suffix ? `?${suffix}` : ""}`,
    CanvasView,
    { timeoutMs: 8_000, ...options },
  );
}

/** M6 只读时间线（契约 M6 §2）：片段 + 台词块，起点由服务端算。 */
export function getTimeline(
  projectId: string,
  params: { episode?: string } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams();
  if (params.episode) query.set("episode", params.episode);
  const suffix = query.toString();
  return request(
    `${projectPath(projectId)}/timeline${suffix ? `?${suffix}` : ""}`,
    TimelineView,
    { timeoutMs: 8_000, ...options },
  );
}

// ---------------------------------------------------------------- 文件（M1 只读用 meta，写入走闸门）

export function getFileMeta(projectId: string, path: string, options: RequestOptions = {}) {
  return request(
    `${projectPath(projectId)}/files/${encodeFilePath(path)}?meta=1`,
    FileMeta,
    options,
  );
}

export function putProjectFile(
  projectId: string,
  path: string,
  input: PutFileInput,
  options: RequestOptions = {},
) {
  return request(`${projectPath(projectId)}/files/${encodeFilePath(path)}`, PutFileOutput, {
    ...options,
    method: "PUT",
    body: input,
  });
}

export function getProjectHistory(
  projectId: string,
  path: string,
  options: RequestOptions = {},
) {
  const query = new URLSearchParams({ path });
  return request(`${projectPath(projectId)}/history?${query.toString()}`, HistoryList, options);
}

// ---------------------------------------------------------------- M4：版本回溯 / 配额 / 导出

/**
 * 项目级版本列表（契约 M4 §1）：`path` 与 `limit` 都可省略。
 * 服务端按 rev 倒序返回；前端仍会自己排一次序，不依赖顺序。
 */
export function listProjectHistory(
  projectId: string,
  params: { path?: string; limit?: number } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams();
  if (params.path) query.set("path", params.path);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return request(
    `${projectPath(projectId)}/history${suffix ? `?${suffix}` : ""}`,
    HistoryList,
    options,
  );
}

/**
 * 撤销「当前用户自己的上一个提交」。
 *
 * 契约只定义了语义，没有冻结响应体（`RevertOutput` 是 /revert 的形状），
 * 所以这里不解析 body：调用方拿到成功信号后重取 `/view` 作为唯一真源。
 */
export function undoProject(projectId: string, options: RequestOptions = {}) {
  return request<void>(`${projectPath(projectId)}/undo`, null, { ...options, method: "POST" });
}

/** 回滚到某个 rev：产生**新的** rev，不是删历史（契约 M4 §1）。 */
export function revertProject(
  projectId: string,
  input: RevertInput,
  options: RequestOptions = {},
) {
  return request(`${projectPath(projectId)}/revert`, RevertOutput, {
    ...options,
    method: "POST",
    body: input,
  });
}

export function getUsage(options: RequestOptions = {}) {
  return request("/usage", UsageSummary, options);
}

export function createExport(
  projectId: string,
  input: CreateExportInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
) {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  return request(`${projectPath(projectId)}/exports`, CreateExportOutput, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export function getExport(projectId: string, exportId: string, options: RequestOptions = {}) {
  return request(
    `${projectPath(projectId)}/exports/${encodeURIComponent(exportId)}`,
    ExportJob,
    options,
  );
}

// ---------------------------------------------------------------- M2：命令层

/**
 * 一次提交多条命令；服务端全有或全无。
 * 409 时错误对象带 `payload.view`，用 `conflictViewFrom()` 取最新投影。
 */
export function postCommands(
  projectId: string,
  input: CommandBatchInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
) {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  return request(`${projectPath(projectId)}/commands`, CommandBatchOutput, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 从 409 响应里取最新故事板投影。服务端可能把它放在
 * 顶层 `view`、`error.details.view`，或直接返回 view（兜底都试一遍）。
 */
export function conflictViewFrom(error: unknown): StoryboardView | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 409 && error.code !== "revision_conflict") return null;

  const candidates: unknown[] = [];
  const payload = error.payload;

  if (isRecord(payload)) {
    candidates.push(payload.view);
    const errorField = payload.error;
    if (isRecord(errorField) && isRecord(errorField.details)) {
      candidates.push(errorField.details.view);
    }
  }
  if (error.details) candidates.push(error.details.view);
  candidates.push(payload);

  for (const candidate of candidates) {
    const parsed = StoryboardView.safeParse(candidate);
    if (parsed.success) return parsed.data;
  }
  return null;
}

// ---------------------------------------------------------------- M2：Agent 会话

export function getAgentView(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/agent/view`, AgentView, options);
}

export function sendAgentMessage(
  projectId: string,
  input: SendAgentMessageInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
) {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  return request(`${projectPath(projectId)}/agent/messages`, SendAgentMessageOutput, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export function abortAgent(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/agent/abort`, OkSchema, {
    ...options,
    method: "POST",
  });
}

// ---------------------------------------------------------------- M2：附件上传

/** multipart 上传；成功后把 `path` 原样放进消息的 attachments。 */
export async function uploadProjectAttachment(
  projectId: string,
  file: File,
  options: RequestOptions = {},
): Promise<UploadOutput> {
  const form = new FormData();
  form.append("file", file);

  let res: Response;
  try {
    res = await fetch(`${API_PREFIX}${projectPath(projectId)}/uploads`, {
      method: "POST",
      body: form,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("network_error", 0, "上传失败，请检查网络后重试");
  }

  if (!res.ok) {
    await throwHttpError(res, options.redirectOnUnauthorized !== false);
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError("invalid_response", res.status, "服务返回的不是合法 JSON");
  }

  const parsed = UploadOutput.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError("invalid_response", res.status, "服务返回的上传结果不符合契约");
  }
  return parsed.data;
}

// ---------------------------------------------------------------- M3：生成流水线

/**
 * 预估点数（不收钱）。请求体与 `POST /generations` 同形，
 * 所以前端「先预估、再确认」两条请求的参数天然一致。
 */
export function estimateGeneration(
  projectId: string,
  input: CreateGenerationInput,
  options: RequestOptions = {},
) {
  return request(`${projectPath(projectId)}/generations/estimate`, EstimateOutput, {
    ...options,
    method: "POST",
    body: input,
  });
}

/** 创建 job：服务端按预估预扣，余额不足 → 402 `insufficient_points`。 */
export function createGeneration(
  projectId: string,
  input: CreateGenerationInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
) {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  return request(`${projectPath(projectId)}/generations`, CreateGenerationOutput, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * 队列全量快照。WS 只给增量，断线重连 / 首屏都以这里为准
 * （契约验收 §8.2：生成中刷新页面，队列状态仍在）。
 */
export function listGenerations(
  projectId: string,
  params: { limit?: number; cursor?: string } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams({ limit: String(params.limit ?? 20) });
  if (params.cursor) query.set("cursor", params.cursor);
  return request(`${projectPath(projectId)}/generations?${query.toString()}`, GenerationList, options);
}

export function getGeneration(projectId: string, jobId: string, options: RequestOptions = {}) {
  return request(
    `${projectPath(projectId)}/generations/${encodeURIComponent(jobId)}`,
    GenerationJob,
    options,
  );
}

/** 取消：queued 全额退；running 只取消未完成的 item（契约 §2）。 */
export function cancelGeneration(projectId: string, jobId: string, options: RequestOptions = {}) {
  return request(
    `${projectPath(projectId)}/generations/${encodeURIComponent(jobId)}/cancel`,
    GenerationJob,
    { ...options, method: "POST" },
  );
}

// ---------------------------------------------------------------- M5：资料库

/** 原样读出 JSON 给调用方自己校验（M5 有几处响应形状比 shared 宽）。 */
const JsonSchema: Schema<unknown> = {
  safeParse: (input: unknown) => ({ success: true, data: input }),
};

const LibraryListSchema: Schema<LibraryEntityViewData[]> = {
  safeParse: (input: unknown) => {
    if (typeof input !== "object" || input === null) {
      return { success: false, error: input };
    }
    return { success: true, data: parseLibraryList(input) };
  },
};

function libraryPath(projectId: string): string {
  return `${projectPath(projectId)}/library`;
}

/**
 * 资料库列表（契约 M5 §1）。
 *
 * `kind` 缺省为 `all`：抽屉的三个 tab 共用一次请求，切 tab 不再打接口。
 */
export function getLibrary(
  projectId: string,
  params: { kind?: LibraryKind | "all" } = {},
  options: RequestOptions = {},
) {
  const query = new URLSearchParams({ kind: params.kind ?? "all" });
  return request(`${libraryPath(projectId)}?${query.toString()}`, LibraryListSchema, options);
}

/** 新建（服务端生成 id）。响应形状未冻结，认不出来就交给调用方重取列表。 */
export async function createLibraryEntity(
  projectId: string,
  input: CreateLibraryInput,
  options: RequestOptions = {},
): Promise<LibraryEntityViewData | null> {
  const payload = await request(`${libraryPath(projectId)}`, JsonSchema, {
    ...options,
    method: "POST",
    body: input,
  });
  return libraryEntityFromPayload(payload);
}

/** 改字段（`PatchLibraryInput`，不含 kind）。 */
export async function patchLibraryEntity(
  projectId: string,
  entityId: string,
  input: PatchLibraryInput,
  options: RequestOptions = {},
): Promise<LibraryEntityViewData | null> {
  const payload = await request(
    `${libraryPath(projectId)}/${encodeURIComponent(entityId)}`,
    JsonSchema,
    { ...options, method: "PATCH", body: input },
  );
  return libraryEntityFromPayload(payload);
}

/**
 * 删除。被镜头引用时服务端返回 409：这里不吞错误，
 * `ApiError.details.referencedBy` / `payload.error.details` 交给 UI 展示。
 */
export function deleteLibraryEntity(
  projectId: string,
  entityId: string,
  options: RequestOptions = {},
) {
  return request<void>(`${libraryPath(projectId)}/${encodeURIComponent(entityId)}`, null, {
    ...options,
    method: "DELETE",
  });
}

/**
 * 上传参考图（multipart，契约 M5 §1 表格最后一行）。
 *
 * 响应体没有冻结：这里不解析，成功后调用方重取列表即可。
 */
export async function uploadLibraryRef(
  projectId: string,
  entityId: string,
  file: File,
  options: RequestOptions = {},
): Promise<void> {
  const form = new FormData();
  form.append("file", file);

  let res: Response;
  try {
    res = await fetch(
      `${API_PREFIX}${libraryPath(projectId)}/${encodeURIComponent(entityId)}/refs`,
      {
        method: "POST",
        body: form,
        credentials: "same-origin",
        signal: options.signal,
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError("network_error", 0, "参考图上传失败，请检查网络后重试");
  }

  if (!res.ok) {
    await throwHttpError(res, options.redirectOnUnauthorized !== false);
  }
}

// ---------------------------------------------------------------- M5：分享

const ShareListSchema: Schema<ShareLink[]> = {
  safeParse: (input: unknown) => {
    if (typeof input !== "object" || input === null) {
      return { success: false, error: input };
    }
    return { success: true, data: parseShareList(input) };
  },
};

/**
 * 生成只读外链（契约 M5 §2）。
 *
 * 后端会返回完整 `{ id, token, url, … }`；缺 `scope / episodeId / allowDownload`
 * 时用请求入参补齐，一次成功的创建不能因为少字段就显示成失败。
 */
export async function createShare(
  projectId: string,
  input: CreateShareInput,
  options: RequestOptions & { idempotencyKey?: string } = {},
): Promise<ShareLink> {
  const { signal, redirectOnUnauthorized, idempotencyKey } = options;
  const payload = await request(`${projectPath(projectId)}/shares`, JsonSchema, {
    method: "POST",
    body: input,
    signal,
    redirectOnUnauthorized,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
  const link = shareLinkFromPayload(payload, input);
  if (!link) throw new ApiError("invalid_response", 200, "服务返回的分享链接不符合契约");
  return link;
}

/**
 * 已有分享列表。
 *
 * 列表里的 `token` / `url` 是**掩码**：完整链接只在创建响应里出现一次。
 * 列表读失败时 UI 降级成一行提示，而不是整层不可用（见 `ShareDialog`）。
 */
export function listShares(projectId: string, options: RequestOptions = {}) {
  return request(`${projectPath(projectId)}/shares`, ShareListSchema, options);
}

/**
 * 撤销：`DELETE /api/shares/:shareId`，用分享记录的 `id`，**不是** token。
 * 成功后公开页立即 404（204 无响应体）。
 */
export function revokeShare(shareId: string, options: RequestOptions = {}) {
  return request<void>(`/shares/${encodeURIComponent(shareId)}`, null, {
    ...options,
    method: "DELETE",
  });
}
